<template>
  <div>
    <CCard>
      <div
        v-if="isLoading"
        :style="{ height: '80vh' }"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <CSpinner
          class="custom-spinner"
          color="info"
          grow
        />
      </div>
  
      <div v-else>
        <CCardHeader>
          <slot name="header">        
            <CIcon
              name="cil-basket"
              class="mr-1"
            /> Health - Medicare Creatives
          </slot>
        </CCardHeader>
  
        <CCardBody>
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center">
              <CSwitch
                variant="3d"
                color="primary"
                horizontal
                size="sm"
                class="mr-2"
                :checked.sync="showRejected"
                @update:checked="loadCreatives"
              />
              <h5 class="m-0">
                Show Rejected
              </h5>
            </div>
            <BulkDownloadCreatives />
          </div>
          <CCallout color="info">
            <small class="text-muted">Total Creatives:</small><br>
            <strong class="h4">
              {{ filteredCount }}
            </strong>
          </CCallout>
          <CDataTable
            small
            border
            sorter
            hover
            column-filter
            :column-filter-value.sync="columnFilterValues"
            :items="formattedReviewedCreatives"
            :fields="fields"
            :items-per-page="10"
            :pagination="{align: 'center'}"
            @filtered-items-change="onFilteredItemsChange"
          >
            <template #id="{item}">
              <td>
                <router-link
                  :to="`/dashboard/creatives/${item.id}`"
                >
                  {{ item.id.substring(0, 10) }}...
                </router-link>
                <a
                  href
                  class="ml-1"
                  @click.prevent="
                    copyIdToClipboard(getSafe(() => item.id))
                  "
                ><CIcon
                  name="cil-copy"
                /></a>
              </td>
            </template>

            <template #accountName="{item}">
              <td>
                <router-link
                  :to="{ name: 'SellerAccountDetails', params: { id: getSafe(() => item.account.id)} }"
                  target="_blank"                 
                >
                  {{ getSafe(() => item.account.name) }}
                </router-link>

                <a
                  href
                  class="ml-1"
                  @click.prevent="
                    copyAccountIdToClipboard(getSafe(() => item.account.id))
                  "
                ><CIcon
                  name="cil-copy"
                /></a>            
              </td>
            </template>        

          

            <template #trafficSourceName-filter>
              <select
                class="form-control form-control-sm"
                @change="trafficSourceFilter = $event.target.value"
              >
                <option value="">
                  All
                </option>
                <option
                  v-for="ts in distinctTrafficSources"
                  :key="ts"
                  :value="ts"
                >
                  {{ ts }}
                </option>
              </select>
            </template>

            <template #trafficSourceName="{item}">
              <td>
                {{ getSafe(() => item.trafficSource.name) }}
              </td>
            </template>

            <template #languageId-filter>
              <select
                class="form-control form-control-sm"
                @change="languageFilter = $event.target.value"
              >
                <option value="">
                  All
                </option>
                <option
                  v-for="lang in distinctLanguages"
                  :key="lang"
                  :value="lang"
                >
                  {{ lang }}
                </option>
              </select>
            </template>

            <template #language="{item}">
              <td>
                {{ getSafe(() => item.language.id) }}
              </td>
            </template>

            <template #stats="{item}">
              <td>
                <div
                  v-if="item.stats"
                  class="w-fixed-130"
                  style="font-size: 0.75rem;"
                >
                  <div>Total: {{ getSafe(() => item.stats.total) }}</div>
                  <div>Pending: {{ getSafe(() => item.stats.pending) }}</div>
                  <div>Approved: {{ getSafe(() => item.stats.approved) }}</div>
                  <div>Rejected: {{ getSafe(() => item.stats.rejected) }}</div>
                </div>
              </td>
            </template>

            <template #status-filter>
              <select
                class="form-control form-control-sm"
                @change="statusFilter = $event.target.value"
              >
                <option value="">
                  All
                </option>
                <option value="Approved">
                  Approved
                </option>
                <option value="Rejected">
                  Rejected
                </option>
                <option value="ReviewPending">
                  ReviewPending
                </option>
              </select>
            </template>

            <template #status="{item}">
              <td>
                <CBadge
                  :color="item.status === 'Approved' ? 'success' : item.status === 'Rejected' ? 'danger' : 'warning'"
                  size="sm"
                >
                  {{ getSafe(() => item.status) }}
                </CBadge>
              </td>
            </template>

            <template #enabledLabel-filter>
              <select
                class="form-control form-control-sm"
                @change="enabledFilter = $event.target.value"
              >
                <option value="">
                  All
                </option>
                <option value="Enabled">
                  Enabled
                </option>
                <option value="Disabled">
                  Disabled
                </option>
              </select>
            </template>

            <template #enabledLabel="{item}">
              <td>
                <CBadge
                  :color="item.enabled ? 'success' : 'danger'"
                  size="sm"
                >
                  {{ item.enabledLabel }}
                </CBadge>
              </td>
            </template>

            <template #marketCategory-filter>
              <select
                class="form-control form-control-sm"
                @change="marketCategoryFilter = $event.target.value"
              >
                <option value="">
                  All
                </option>
                <option value="Marketing">
                  Marketing
                </option>
                <option value="Communication">
                  Communication
                </option>
                <option value="Unknown">
                  Unknown
                </option>
              </select>
            </template>

            <template #marketCategory="{item}">
              <td class="text-center">
                <span
                  v-if="item.marketCategory === 'Marketing'"
                  v-c-tooltip="'Marketing'"
                  class="badge"
                  style="background-color: rgba(255, 152, 0, 0.2); color: #e68a00;"
                >
                  M
                </span>
                <span
                  v-else-if="item.marketCategory === 'Communication'"
                  v-c-tooltip="'Communication'"
                  class="badge"
                  style="background-color: rgba(51, 153, 255, 0.2); color: #2982cc;"
                >
                  C
                </span>
                <span
                  v-else
                  v-c-tooltip="'Unknown'"
                  class="badge"
                  style="background-color: rgba(136, 136, 136, 0.2); color: #888;"
                >
                  ?
                </span>
              </td>
            </template>

            <template #formattedCreationDate="{item}">
              <td style="font-size: 0.75rem;">
                {{ item.formattedCreationDate }}
              </td>
            </template>

            <template #Actions="{item}">
              <td>
                <CButton
                  v-if="item.enabled"
                  color="warning"
                  size="sm"
                  style="font-size: 0.7rem; padding: 0.15rem 0.4rem;"
                  @click="showDisableModal(item)"
                >
                  Disable
                </CButton>
                <CButton
                  v-else
                  color="success"
                  size="sm"
                  style="font-size: 0.7rem; padding: 0.15rem 0.4rem;"
                  @click="showEnableModal(item)"
                >
                  Enable
                </CButton>
              </td>          
            </template>
          </CDataTable>
        </CCardBody>
      </div>
    </CCard>

    <CModal
      title="Confirm enabling creative"
      :show.sync="warningEnableModal"
    >
      Are you sure you want to enable this creative?

      <template #footer>
        <ButtonLoading
          color="success"
          :loading="isLoading"
          @click="enableCreativeFromModal"
        >
          Enable
        </ButtonLoading>

        <CButton
          color="danger"
          @click="warningEnableModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Confirm disabling creative"
      :show.sync="warningDisableModal"
    >
      Are you sure you want to disable this creative?

      <template #footer>
        <ButtonLoading
          color="warning"
          :loading="isLoading"
          @click="disableCreativeFromModal"
        >
          Disable
        </ButtonLoading>

        <CButton
          color="danger"
          @click="warningDisableModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>
  
<script>

import { mapActions, mapState } from 'vuex'
import { showSuccessMessage, showErrorMessage } from '@/notification-utils'
import { dateTimeFormat } from '@/utils'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import BulkDownloadCreatives from './BulkDownloadCreatives.vue'
  
export default {
  name: 'Creatives',
  
  title: 'Creatives',  

  components: { BulkDownloadCreatives },

  mixins: [copyToClipboardMixin],
    
  data() {
    return {
      type: '',
      isLoading: false,
      showRejected: false,
      warningEnableModal: false,
      warningDisableModal: false,
      selectedCreative: {},
      marketCategoryFilter: '',
      statusFilter: '',
      enabledFilter: '',
      trafficSourceFilter: '',
      languageFilter: '',
      columnFilterValues: {},
      filteredCount: 0
    }
  },

  watch: {
    marketCategoryFilter(val) {
      this.columnFilterValues = {
        ...this.columnFilterValues,
        marketCategory: val
      }
    },
    statusFilter(val) {
      this.columnFilterValues = {
        ...this.columnFilterValues,
        status: val
      }
    },
    enabledFilter(val) {
      this.columnFilterValues = {
        ...this.columnFilterValues,
        enabledLabel: val
      }
    },
    trafficSourceFilter(val) {
      this.columnFilterValues = {
        ...this.columnFilterValues,
        trafficSourceName: val
      }
    },
    languageFilter(val) {
      this.columnFilterValues = {
        ...this.columnFilterValues,
        languageId: val
      }
    }
  },
  
  computed: {
    ...mapState('creative', ['reviewedCreatives']),
  
    fields () {
      return [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'accountName', label: 'Account', sortable: true },
        { key: 'externalId', label: 'External ID', sortable: true },        
        { key: 'trafficSourceName', label: 'Traffic Source', sortable: true },               
        { key: 'languageId', label: 'Language', sortable: true },               
        { key: 'status', label: 'Status', sortable: true },          
        { key: 'enabledLabel', label: 'Enabled', sortable: true },          
        { key: 'marketCategory', label: 'Type', sortable: true },          
        { key: 'stats', label: 'Buyer Approval Statistics', filter: false, sorter: false },               
        { key: 'formattedCreationDate', label: 'Created At', sorter: true },               
        { key: 'Actions', filter: false }
      ]
    },
   
    distinctTrafficSources() {
      const values = this.reviewedCreatives
        .map(c => this.getSafe(() => c.trafficSource.name))
        .filter(Boolean)
      return [...new Set(values)].sort()
    },

    distinctLanguages() {
      const values = this.reviewedCreatives
        .map(c => this.getSafe(() => c.language.id))
        .filter(Boolean)
      return [...new Set(values)].sort()
    },

    formattedReviewedCreatives() {
      return this.reviewedCreatives        
        .map(creative => {
          return {
            ...creative,
            formattedCreationDate: creative.creationDate ? dateTimeFormat.format(new Date(creative.creationDate)) : '',
            accountName: this.getSafe(() => creative.account.name),
            trafficSourceName: this.getSafe(() => creative.trafficSource.name),
            languageId: this.getSafe(() => creative.language.id),
            enabledLabel: creative.enabled ? 'Enabled' : 'Disabled'
          }
        })
    },      
  },
  
  async created() {
    await this.loadCreatives()
  }, 

  methods: {
    ...mapActions('creative', ['getReviewedCreatives','enableCreative','disableCreative']),

    onFilteredItemsChange(items) {
      this.filteredCount = items.length
    },

    async loadCreatives() {
      try {
        this.isLoading = true
        await this.getReviewedCreatives(this.showRejected)
      } finally {
        this.isLoading = false
      }
    },

    copyIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Creative ID copied to clipboard')
    },

    copyAccountIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Account ID copied to clipboard')
    },

    showEnableModal (creative) {
      this.selectedCreative = creative
      this.warningEnableModal = true
    },

    showDisableModal (creative) {
      this.selectedCreative = creative
      this.warningDisableModal = true
    },

    async enableCreativeFromModal() {
      try {
        this.isLoading = true
        await this.enableCreative(this.selectedCreative.id)
        showSuccessMessage("Successfully enabled creative")
        this.selectedCreative.enabled = true
        this.selectedCreative.enabledLabel = 'Enabled'
      } catch(error) {
        showErrorMessage("Failed to enable creative")
      } finally {
        this.warningEnableModal = false
        this.isLoading = false
      }
    },

    async disableCreativeFromModal() {
      try {
        this.isLoading = true
        await this.disableCreative(this.selectedCreative.id)

        showSuccessMessage("Successfully disabled creative")
        this.selectedCreative.enabled = false
        this.selectedCreative.enabledLabel = 'Disabled'
      } catch(error) {
        showErrorMessage("Failed to disable creative")
      } finally {
        this.warningDisableModal = false
        this.isLoading = false
      }
    }
  }
}
</script>
  
  <style></style>
  